import { useDocumentVisibility, useWindowFocus } from "@vueuse/core";
import moment, { type Moment } from "moment";
import { nextTick, watchEffect } from "vue";

import { backendOld } from "~/api";
import { registerAllWebMcpTools, unregisterAllWebMcpTools } from "~/api/webmcp/register";
import { notify } from "~/components/notifications";
import { NotificationType, StoreDataPreservationMode } from "~/shared/enums";
import type { User } from "~/shared/types";
import { useAppStore, useDataStore, useEnvironmentStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { deleteCookie, openPathInDesktop } from "~/utils/common";
import { setSentryUser } from "~/utils/exceptionClient";
import { getReusableLoginToken } from "~/utils/localAgentInstallCommand";
import {
  COLLECT_ALL_DATA_KEY,
  LAST_LOCAL_DATA_RESET_AT_KEY,
  lsClearAll,
  lsGet,
  lsSet,
  USER_DUID_LOCAL_STORAGE_KEY,
} from "~/utils/localStorageManager";

import type { Actions } from ".";

const MAX_IDLE_TIME_MS = 1000 * 60 * 5;

/** Local app related actions */
export default (actions: Actions) => ({
  /** Clear all local data */
  clearAllLocalData: (hard = true): void => {
    lsClearAll();
    if (!hard) {
      return;
    }
    lsSet(LAST_LOCAL_DATA_RESET_AT_KEY, moment().toISOString());
    window.location.reload();
  },
  setUser: (user: User): void => {
    const environmentStore = useEnvironmentStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    userStore.setUser(user);
    usePageStore().updateTheme();
    const userObj = {
      id: user.duid,
      email: user.email,
      name: user.name,
      displayName: user.name,
      tenantName: tenantStore.name,
      tenantDuid: tenantStore.duid,
      environment: environmentStore.name,
    };

    // set tracking
    setSentryUser(userObj);
  },
  setUserData: (
    data: Record<string, never>,
    preservationMode: StoreDataPreservationMode = StoreDataPreservationMode.NONE
  ): void => {
    const appStore = useAppStore();
    const pageStore = usePageStore();
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    if (!data.isLoggedIn) {
      return;
    }

    const start = performance.now();

    const resetLocalDataBeforeStr = data.resetLocalDataBeforeStr as string | null;
    const lastLocalDataResetStr = lsGet(LAST_LOCAL_DATA_RESET_AT_KEY);
    if (!lastLocalDataResetStr) {
      lsSet(LAST_LOCAL_DATA_RESET_AT_KEY, moment().toISOString());
    } else if (resetLocalDataBeforeStr && lastLocalDataResetStr < resetLocalDataBeforeStr) {
      actions.app.clearAllLocalData(false);
    }

    const collectAllData = data.collectAllData as boolean;
    const localCollectAllData = lsGet(COLLECT_ALL_DATA_KEY) === "true";
    if (localCollectAllData !== collectAllData) {
      lsSet(COLLECT_ALL_DATA_KEY, collectAllData.toString());
      if (collectAllData) {
        // TODO this isn't great UX
        window.location.reload();
      }
    }

    pageStore.isLoggedIn = true;
    registerAllWebMcpTools();

    environmentStore.setBackendEnvironment(data.environment);

    appStore.init();
    appStore.setEventKinds(data.eventKinds);
    appStore.setPageIconOptions(data.pageIconOptions);
    appStore.setWebhookEventKinds(data.webhookEventKinds);

    tenantStore.setTenant(data.tenant);

    userStore.setFirstDayOfWeek(data.firstDayOfWeek);
    userStore.setLastUrlPath(data.lastUrlPath);
    userStore.setNotificationSettings(data.notificationSettings);
    userStore.setOpenInNativeApp(data.openInNativeApp);
    userStore.setHasPassword(data.hasPassword);

    // these groups must be loaded in this order for subsequent comparisons
    dataStore.$setSpaces(data.spaces);
    dataStore.$setDartboards(data.dartboards);
    dataStore.$setRelationshipKinds(data.relationshipKinds);

    dataStore.$setProperties(data.properties);
    dataStore.$setFolders(data.folders);

    if (data.draftTask) {
      dataStore.$setDraftTask(data.draftTask);
    }

    // then these are just alphabetical
    dataStore.$setAutomations(data.automations);
    dataStore.$setConnectorAuths(data.connectorAuths);
    dataStore.$setConnectors(data.connectors);
    dataStore.$setDashboards(data.dashboards);
    dataStore.$setFormFields(data.formFields);
    dataStore.$setForms(data.forms);
    dataStore.$setLayouts(data.layouts, preservationMode);
    dataStore.$setOptions(data.options);
    dataStore.$setSkills(data.skills);
    dataStore.$setStatuses(data.statuses);
    dataStore.$setTaskKinds(data.taskKinds);
    dataStore.$setUnreadNotificationCount(data.unreadNotificationsCount);
    dataStore.$setUsers(data.users);
    dataStore.$setViews(data.views);
    dataStore.$setWebhooks(data.webhooks);

    actions.app.setUser(data.user);

    pageStore.logTime("setting user data", start);
  },
  watchForIdle: (): void => {
    const visibility = useDocumentVisibility();
    const windowFocus = useWindowFocus();
    const appStore = useAppStore();
    const pageStore = usePageStore();

    let idleStart: undefined | Moment;

    if (!pageStore.isLoggedIn || pageStore.isPublic) {
      return;
    }

    watchEffect(async () => {
      if (visibility.value === "hidden" || !windowFocus.value) {
        idleStart = moment();
        return;
      }
      if (idleStart === undefined || moment().diff(idleStart) <= MAX_IDLE_TIME_MS) {
        idleStart = undefined;
        return;
      }

      const syncSince = idleStart;
      idleStart = undefined;
      if (!appStore.websocketManager) {
        return;
      }
      await appStore.websocketManager.syncChangesSince(syncSince);
    });
  },
  openInDesktopApp: async (): Promise<void> => {
    const dataStore = useDataStore();
    const pageStore = usePageStore();
    const userStore = useUserStore();
    const router = dataStore.$router;

    const { token, tokenPersistence } = getReusableLoginToken(userStore.duid);
    try {
      await tokenPersistence;
    } catch {
      notify({ message: "Couldn't open the desktop app.", type: NotificationType.ERROR });
      return;
    }

    pageStore.redirectedToNative = true;
    await openPathInDesktop(
      router.resolve({
        name: "login",
        query: { t: token, next: encodeURIComponent(router.currentRoute.value.fullPath.substring(1)) },
      }).fullPath
    );
  },
  /** Logout the user, reset stores and clear all local data  */
  logout: async (): Promise<void> => {
    const appStore = useAppStore();
    const pageStore = usePageStore();
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    if (!pageStore.isLoggedIn) {
      return;
    }
    actions.app.clearAllLocalData(false);
    appStore.disconnectWs();

    pageStore.isLoggedIn = false;
    unregisterAllWebMcpTools();
    // do this after setting pageStore.isLoggedIn to false to avoid rendering errors
    nextTick(() => {
      // reset all of the stores that need it (not EnvironmentStore nor PageStore)
      appStore.$reset();
      dataStore.$reset();
      environmentStore.setBackendEnvironment(undefined);
      tenantStore.$reset();
      userStore.$reset();

      lsSet(USER_DUID_LOCAL_STORAGE_KEY, "");

      pageStore.updateTheme(true);
    });

    try {
      await backendOld.auth.logout();
    } catch (error) {
      deleteCookie("sessionid");
      throw error;
    }
  },
});
